import React, { useState, useEffect } from 'react';
import { User, ArrowLeft, ArrowRight, Loader2, Phone, Mail } from 'lucide-react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

declare var MojoAuth: any;

interface SmartMojoAuthProps {
  userType?: 'new' | 'existing';
  onSuccess: (userData: any) => void;
  onError: (message: string) => void;
  onBack: () => void;
}

export const SmartMojoAuth: React.FC<SmartMojoAuthProps> = ({ 
  userType = 'new',
  onSuccess, 
  onError, 
  onBack 
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [showMojoUI, setShowMojoUI] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [mojoResponse, setMojoResponse] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    alternativeMobile: '',
    address: '',
    pincode: '',
    state: '',
    district: ''
  });

  // Mojo Auth client ID
  const MOJO_CLIENT_ID = 'bac4b624-3374-4889-a893-ac8df440e424';

  // Always start with Mojo Auth verification first as requested
  useEffect(() => {
    setShowMojoUI(true);
    handleMojoAuth();
  }, []);

  const loadMojoAuthScript = () => {
    return new Promise((resolve, reject) => {
      if (window.MojoAuth) {
        resolve(window.MojoAuth);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.mojoauth.com/js/mojoauth.min.js';
      script.async = true;
      
      script.onload = () => resolve(window.MojoAuth);
      script.onerror = () => reject(new Error('Failed to load Mojo Auth script'));
      
      document.head.appendChild(script);
    });
  };

  const handleMojoAuth = async () => {
    setError(null);
    setIsVerifying(true);

    try {
      if (!window.MojoAuth) {
        await loadMojoAuthScript();
      }

      const mojoauth = new window.MojoAuth(MOJO_CLIENT_ID, {
        source: [{ type: 'email', feature: 'otp' }],
        container: 'mojoauth-passwordless-form'
      });

      mojoauth.signIn().then(async (response: any) => {
        setIsVerifying(false);
        setShowMojoUI(false);
        
        if (response.authenticated) {
          const email = response.user.email.toLowerCase();
          setVerifiedEmail(email);
          setMojoResponse(response);
          
          // After Mojo verifies email, check if user exists in Firebase
          await checkUserExists(email, response);
        } else {
          onError('Verification failed');
        }
      }).catch((error: any) => {
        console.error('Mojo Auth error:', error);
        setIsVerifying(false);
        setShowMojoUI(false);
        setError(error.message || 'Verification failed');
      });
      
    } catch (error: any) {
      console.error('Mojo Auth error:', error);
      setIsVerifying(false);
      setShowMojoUI(false);
      setError(error.message || 'Security gateway error');
    }
  };

  const checkUserExists = async (email: string, response: any) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', email));
      
      if (userDoc.exists()) {
        // User exists - login successful
        const userData = userDoc.data();
        const token = response.token || 'verified_' + Date.now();
        
        const finalUserData = {
          ...userData,
          lastLoginAt: new Date().toISOString(),
          verified: true,
          token: token
        };

        // Save to localStorage
        localStorage.setItem('user', JSON.stringify(finalUserData));
        onSuccess(finalUserData);
      } else {
        // User doesn't exist - show registration form
        setShowForm(true);
      }
    } catch (error) {
      console.error('Error checking user:', error);
      onError('Failed to verify user');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.firstName.trim() || formData.firstName.length > 10) {
      setError('First name is required (max 10 chars)');
      return;
    }
    
    if (!formData.lastName.trim() || formData.lastName.length > 10) {
      setError('Last name is required (max 10 chars)');
      return;
    }
    
    if (!formData.mobile.trim() || !/^\d{10}$/.test(formData.mobile)) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }

    if (formData.alternativeMobile && !/^\d{10}$/.test(formData.alternativeMobile)) {
      setError('Enter a valid 10-digit alternative mobile number');
      return;
    }

    if (!formData.address.trim()) {
      setError('Address is required');
      return;
    }

    if (!formData.pincode.trim() || !/^\d{6}$/.test(formData.pincode)) {
      setError('Enter a valid 6-digit pincode');
      return;
    }

    if (!formData.district.trim()) {
      setError('District is required');
      return;
    }

    if (!formData.state.trim()) {
      setError('State is required');
      return;
    }

    if (!verifiedEmail || !mojoResponse) {
      setError('Session expired. Please verify email again.');
      setShowForm(false);
      setShowMojoUI(true);
      handleMojoAuth();
      return;
    }

    try {
      const token = mojoResponse.token || 'verified_' + Date.now();
      
      const userData = {
        email: verifiedEmail,
        fullName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        mobile: formData.mobile,
        alternativeMobile: formData.alternativeMobile,
        shippingAddress: {
          fullName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
          address: formData.address.trim(),
          pincode: formData.pincode.trim(),
          state: formData.state.trim(),
          district: formData.district.trim(),
          mobile: formData.mobile
        },
        verified: true,
        token: token,
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        isConsultant: false,
        isBanned: false
      };

      // Save to Firebase - Check again to prevent duplicate saves
      const userDoc = await getDoc(doc(db, 'users', verifiedEmail));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', verifiedEmail), userData);
      }
      
      // Save to localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      onSuccess(userData);
    } catch (error) {
      console.error('Error saving new user:', error);
      setError('Failed to save user data');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'mobile' || field === 'alternativeMobile' || field === 'pincode') {
      value = value.replace(/\D/g, '');
      if (field === 'pincode') value = value.slice(0, 6);
      else value = value.slice(0, 10);
    } else if (field === 'firstName' || field === 'lastName') {
      value = value.slice(0, 10);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="w-full">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      {showMojoUI && (
        <div className="space-y-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="text-orange-600" size={24} />
            </div>
            <h3 className="text-lg font-bold text-stone-900 uppercase tracking-tight">
              Verify Your Email
            </h3>
            <p className="text-sm text-stone-500">
              Complete the verification to continue
            </p>
          </div>
          
          <div id="mojoauth-passwordless-form" className="w-full"></div>
          
          {isVerifying && (
            <div className="flex items-center justify-center gap-2 text-orange-600">
              <Loader2 className="animate-spin" size={16} />
              <span className="text-sm font-medium">Initializing gateway...</span>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="text-orange-600" size={24} />
            </div>
            <h3 className="text-lg font-bold text-stone-900 uppercase tracking-tight">
              Complete Registration
            </h3>
            <p className="text-sm text-stone-500">
              Tell us a bit about yourself
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">
                First Name
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Max 10 chars"
                className="w-full px-4 py-3 border border-orange-200 rounded-xl bg-white text-sm font-medium outline-none focus:border-orange-400"
                maxLength={10}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Max 10 chars"
                className="w-full px-4 py-3 border border-orange-200 rounded-xl bg-white text-sm font-medium outline-none focus:border-orange-400"
                maxLength={10}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">
              Mobile Number
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-medium text-sm">
                +91
              </span>
              <input
                type="tel"
                value={formData.mobile}
                onChange={(e) => handleInputChange('mobile', e.target.value)}
                placeholder="10 digit number"
                className="w-full pl-12 pr-4 py-3 border border-orange-200 rounded-xl bg-white text-sm font-medium outline-none focus:border-orange-400"
                maxLength={10}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">
              Alternative Mobile (Optional)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-medium text-sm">
                +91
              </span>
              <input
                type="tel"
                value={formData.alternativeMobile}
                onChange={(e) => handleInputChange('alternativeMobile', e.target.value)}
                placeholder="10 digit number"
                className="w-full pl-12 pr-4 py-3 border border-orange-200 rounded-xl bg-white text-sm font-medium outline-none focus:border-orange-400"
                maxLength={10}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">
              Full Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder="House/Street/Area"
              className="w-full px-4 py-3 border border-orange-200 rounded-xl bg-white text-sm font-medium outline-none focus:border-orange-400"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">
                Pincode
              </label>
              <input
                type="text"
                value={formData.pincode}
                onChange={(e) => handleInputChange('pincode', e.target.value)}
                placeholder="6 digits"
                className="w-full px-4 py-3 border border-orange-200 rounded-xl bg-white text-sm font-medium outline-none focus:border-orange-400"
                maxLength={6}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">
                City/District
              </label>
              <input
                type="text"
                value={formData.district}
                onChange={(e) => handleInputChange('district', e.target.value)}
                placeholder="Your City"
                className="w-full px-4 py-3 border border-orange-200 rounded-xl bg-white text-sm font-medium outline-none focus:border-orange-400"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">
              State
            </label>
            <input
              type="text"
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              placeholder="Your State"
              className="w-full px-4 py-3 border border-orange-200 rounded-xl bg-white text-sm font-medium outline-none focus:border-orange-400"
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-orange-600 text-white py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-orange-200"
          >
            Complete Registration
            <ArrowRight size={16} />
          </button>
        </form>
      )}

      <button
        onClick={onBack}
        className="w-full mt-4 py-3 text-stone-400 font-bold uppercase tracking-widest text-[10px] hover:text-stone-600 transition-colors"
      >
        Cancel and Back
      </button>
    </div>
  );
};
